import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import { FilterSearchInput } from "@/components/ui/filter-search-input";
import { cn, parseDateAsLocal } from "@/lib/utils";
import { format } from "date-fns";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ColumnPresenceFilterChoices } from "./column-presence-filter";
import {
  EMPTY_FILTER_VALUE,
  NOT_BLANK_FILTER_VALUE,
  displayValueInList,
  displayValuesEqual,
  encodeContainsFilterValue,
  withoutPresenceFilterValues,
} from "./dashboard-column-filter-match";
import {
  type ColumnProperties,
  readDisplayedColumnOrder,
} from "./dashboard-table";
import {
  buildDateRangeFilterValue,
  parseDateRangeFilterValue,
  resolveColumnFilterType,
} from "./dashboard-table-date-filter";

interface DashboardMapFacetRailProps<T, K extends string> {
  data: T[];
  accessorKeys: readonly K[];
  getColumnProperties: (accessorKey: K) => ColumnProperties<T, K>;
  columnFilters: Record<string, string[]>;
  onColumnFiltersChange: (filters: Record<string, string[]>) => void;
  onFilterValuesRequest?: (columnId: string, search?: string) => void;
  /**
   * Same key the list `DashboardTable` persists under. Filter sections follow
   * that saved column order. Omit to keep accessor-key order.
   */
  tablePersistenceKey?: string;
  className?: string;
}

function filterableKeysInColumnOrder<K extends string>(
  columnOrder: readonly string[],
  accessorKeys: readonly K[],
  isFilterable: (key: K) => boolean,
): K[] {
  const filterable = new Set<string>();
  for (const key of accessorKeys) {
    if (isFilterable(key)) filterable.add(key);
  }
  const seen = new Set<string>();
  const ordered: K[] = [];
  for (const key of columnOrder) {
    if (!filterable.has(key) || seen.has(key)) continue;
    seen.add(key);
    ordered.push(key as K);
  }
  for (const key of accessorKeys) {
    if (filterable.has(key) && !seen.has(key)) ordered.push(key);
  }
  return ordered;
}

function displayFacetValue(value: string): string {
  return value.trim() === "" ? "(Empty)" : value;
}

export function DashboardMapFacetRail<T, K extends string>({
  data,
  accessorKeys,
  getColumnProperties,
  columnFilters,
  onColumnFiltersChange,
  onFilterValuesRequest,
  tablePersistenceKey,
  className,
}: DashboardMapFacetRailProps<T, K>) {
  const [expanded, setExpanded] = useState<Set<string>>(
    () =>
      new Set(
        Object.entries(columnFilters)
          .filter(([, values]) => values.length > 0)
          .map(([columnId]) => columnId),
      ),
  );
  const [searchByColumn, setSearchByColumn] = useState<Record<string, string>>(
    {},
  );

  useEffect(() => {
    setExpanded((current) => {
      let changed = false;
      const next = new Set(current);
      for (const [columnId, values] of Object.entries(columnFilters)) {
        if (values.length > 0 && !next.has(columnId)) {
          next.add(columnId);
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [columnFilters]);

  const filterableKeys = useMemo(() => {
    const columnOrder = tablePersistenceKey
      ? readDisplayedColumnOrder(tablePersistenceKey, accessorKeys)
      : accessorKeys;
    return filterableKeysInColumnOrder(
      columnOrder,
      accessorKeys,
      (key) => getColumnProperties(key).filterable !== false,
    );
  }, [accessorKeys, getColumnProperties, tablePersistenceKey]);

  const toggleExpanded = (columnId: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(columnId)) {
        next.delete(columnId);
      } else {
        next.add(columnId);
      }
      return next;
    });
  };

  const setColumnFilter = (columnId: string, values: string[] | undefined) => {
    const next = { ...columnFilters };
    if (values && values.length > 0) next[columnId] = values;
    else delete next[columnId];
    onColumnFiltersChange(next);
  };

  return (
    <aside
      aria-label="Map filters"
      className={cn(
        "flex h-full max-h-full min-h-0 w-72 shrink-0 self-stretch flex-col overflow-hidden rounded-md border bg-background",
        className,
      )}
    >
      <div className="border-b px-3 py-2">
        <h3 className="text-sm font-semibold">Filters</h3>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {filterableKeys.map((accessorKey) => {
          const columnId = String(accessorKey);
          const properties = getColumnProperties(accessorKey);
          const values = columnFilters[columnId] ?? [];
          const isExpanded = expanded.has(columnId);
          const filterType = resolveColumnFilterType(
            columnId,
            properties.filterType,
          );
          return (
            <section key={columnId} className="border-b last:border-b-0">
              <div className="flex items-center">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/60"
                  aria-expanded={isExpanded}
                  onClick={() => toggleExpanded(columnId)}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  )}
                  <span className="min-w-0 flex-1 truncate">
                    {properties.displayName}
                  </span>
                  {values.length > 0 ? (
                    <span className="rounded-full bg-primary px-1.5 py-0.5 text-xs text-primary-foreground">
                      {values.length}
                    </span>
                  ) : null}
                </button>
              </div>
              {isExpanded ? (
                filterType === "dateRange" ? (
                  <MapDateFacet
                    columnId={columnId}
                    values={values}
                    allowBlankFilter={properties.allowBlankFilter !== false}
                    onChange={(next) => setColumnFilter(columnId, next)}
                  />
                ) : (
                  <MapValueFacet
                    data={data}
                    accessorKey={accessorKey}
                    properties={properties}
                    values={values}
                    search={searchByColumn[columnId] ?? ""}
                    onSearchChange={(search) => {
                      setSearchByColumn((current) => ({
                        ...current,
                        [columnId]: search,
                      }));
                    }}
                    onSearchRequest={
                      onFilterValuesRequest
                        ? (search) =>
                            onFilterValuesRequest(
                              columnId,
                              search.trim() || undefined,
                            )
                        : undefined
                    }
                    onChange={(next) => setColumnFilter(columnId, next)}
                  />
                )
              ) : null}
            </section>
          );
        })}
      </div>
    </aside>
  );
}

function MapValueFacet<T, K extends string>({
  data,
  accessorKey,
  properties,
  values,
  search,
  onSearchChange,
  onSearchRequest,
  onChange,
}: {
  data: T[];
  accessorKey: K;
  properties: ColumnProperties<T, K>;
  values: string[];
  search: string;
  onSearchChange: (search: string) => void;
  onSearchRequest?: (search: string) => void;
  onChange: (values: string[] | undefined) => void;
}) {
  const searchRequestRef = useRef(onSearchRequest);
  searchRequestRef.current = onSearchRequest;
  useEffect(() => {
    if (!searchRequestRef.current) return;
    const timer = window.setTimeout(
      () => searchRequestRef.current?.(search),
      250,
    );
    return () => window.clearTimeout(timer);
  }, [search]);

  const distinctValues = useMemo(() => {
    const source =
      properties.filterValues && properties.filterValues.length > 0
        ? properties.filterValues
        : data.map((row) => properties.getValue(row, accessorKey));
    const valuesByKey = new Map<string, string>();
    for (const rawValue of withoutPresenceFilterValues(source)) {
      const displayValue = displayFacetValue(rawValue);
      if (
        displayValue === EMPTY_FILTER_VALUE ||
        displayValue === NOT_BLANK_FILTER_VALUE
      ) {
        continue;
      }
      valuesByKey.set(
        displayValue.trim().replace(/\s+/g, " ").toLocaleLowerCase(),
        displayValue,
      );
    }
    return [...valuesByKey.values()].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [accessorKey, data, properties]);
  const filteredValues = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return distinctValues;
    return distinctValues.filter((value) =>
      value.toLowerCase().includes(query),
    );
  }, [distinctValues, search]);

  const toggleValue = (value: string) => {
    const next = displayValueInList(values, value)
      ? values.filter((candidate) => !displayValuesEqual(candidate, value))
      : [...values, value];
    onChange(next.length > 0 ? next : undefined);
  };

  return (
    <div className="space-y-2 border-t bg-muted/20 p-2">
      <div className="flex items-center gap-1">
        <FilterSearchInput
          placeholder="Search values..."
          value={search}
          onValueChange={onSearchChange}
        />
        {values.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={`Clear ${properties.displayName} filter`}
            onClick={() => onChange(undefined)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>
      {properties.allowBlankFilter !== false ? (
        <ColumnPresenceFilterChoices
          idPrefix={`map-facet-presence-${String(accessorKey)}`}
          values={values}
          onToggle={toggleValue}
        />
      ) : null}
      <div className="max-h-52 space-y-0.5 overflow-y-auto">
        {search.trim() !== "" ? (
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded px-1 py-1 text-left text-xs hover:bg-muted"
            onClick={() =>
              toggleValue(encodeContainsFilterValue(search.trim()))
            }
          >
            Use “{search.trim()}”
          </button>
        ) : null}
        {filteredValues.map((value) => (
          <label
            key={value}
            htmlFor={`map-facet-${String(accessorKey)}-${encodeURIComponent(value)}`}
            className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs hover:bg-muted"
          >
            <Checkbox
              id={`map-facet-${String(accessorKey)}-${encodeURIComponent(value)}`}
              checked={displayValueInList(values, value)}
              onCheckedChange={() => toggleValue(value)}
            />
            <span className="min-w-0 flex-1 break-words">{value}</span>
          </label>
        ))}
        {filteredValues.length === 0 && search.trim() === "" ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">
            No values available.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function MapDateFacet({
  columnId,
  values,
  allowBlankFilter = true,
  onChange,
}: {
  columnId: string;
  values: string[];
  allowBlankFilter?: boolean;
  onChange: (values: string[] | undefined) => void;
}) {
  const bounds = parseDateRangeFilterValue(values);
  const update = (
    from: string | null,
    to: string | null,
    includeBlank = Boolean(bounds.includeBlank),
    includeNotBlank = Boolean(bounds.includeNotBlank),
  ) =>
    onChange(
      buildDateRangeFilterValue(from, to, includeBlank, includeNotBlank),
    );

  return (
    <div className="space-y-3 border-t bg-muted/20 p-3">
      <div className="space-y-1">
        <span className="text-xs font-medium text-muted-foreground">From</span>
        <DatePicker
          value={bounds.from ? parseDateAsLocal(bounds.from) : null}
          onChange={(date) =>
            update(date ? format(date, "yyyy-MM-dd") : null, bounds.to)
          }
          placeholder="Any date"
        />
      </div>
      <div className="space-y-1">
        <span className="text-xs font-medium text-muted-foreground">To</span>
        <DatePicker
          value={bounds.to ? parseDateAsLocal(bounds.to) : null}
          onChange={(date) =>
            update(bounds.from, date ? format(date, "yyyy-MM-dd") : null)
          }
          placeholder="Any date"
        />
      </div>
      {allowBlankFilter ? (
        <>
          <label
            htmlFor={`map-facet-blank-${columnId}`}
            className="flex cursor-pointer items-center gap-2 text-xs"
          >
            <Checkbox
              id={`map-facet-blank-${columnId}`}
              checked={Boolean(bounds.includeBlank)}
              onCheckedChange={(checked) =>
                update(
                  bounds.from,
                  bounds.to,
                  checked === true,
                  bounds.includeNotBlank,
                )
              }
            />
            Blank
          </label>
          <label
            htmlFor={`map-facet-not-blank-${columnId}`}
            className="flex cursor-pointer items-center gap-2 text-xs"
          >
            <Checkbox
              id={`map-facet-not-blank-${columnId}`}
              checked={Boolean(bounds.includeNotBlank)}
              onCheckedChange={(checked) =>
                update(
                  bounds.from,
                  bounds.to,
                  bounds.includeBlank,
                  checked === true,
                )
              }
            />
            Not blank
          </label>
        </>
      ) : null}
      {values.length > 0 ? (
        <Button
          type="button"
          variant="link"
          size="xs"
          className="h-auto p-0"
          onClick={() => onChange(undefined)}
        >
          Clear filter
        </Button>
      ) : null}
    </div>
  );
}
